import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Printer, Flag, CheckCircle, Truck, XCircle } from "@phosphor-icons/react";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }) : "");

export default function PurchaseOrderTimeline() {
  const { id } = useParams();
  const [po, setPo] = useState(undefined); // undefined = loading, null = not found

  useEffect(() => {
    api.get(`/purchase-orders/${id}`).then((r) => setPo(r.data)).catch(() => setPo(null));
  }, [id]);

  const buildEvents = (p) => {
    const items = p.items || [];
    const ordered = items.reduce((s, it) => s + (it.quantity || 0), 0);
    const events = [
      { type: "start", at: p.created_at, by: p.by_name, label: `Order raised — ${ordered} pcs across ${items.length} item(s)`, sub: p.supplier_name },
    ];
    (p.receipts || []).forEach((rc, ri) => {
      const nets = (rc.quantities || []);
      const rejs = (rc.rejected || []);
      const netLines = nets.map((q, qi) => ({ q, name: items[qi]?.material_name, unit: items[qi]?.unit })).filter((l) => l.q > 0);
      const rejLines = rejs.map((q, qi) => ({ q, name: items[qi]?.material_name, unit: items[qi]?.unit })).filter((l) => l.q > 0);
      const totalNet = netLines.reduce((s, l) => s + l.q, 0);
      events.push({
        type: "delivery", at: rc.at, by: rc.by_name,
        label: `Delivery ${ri + 1} — ${totalNet} pcs received into stock`,
        lines: netLines,
      });
      if (rejLines.length > 0) {
        const totalRej = rejLines.reduce((s, l) => s + l.q, 0);
        events.push({
          type: "reject", at: rc.at, by: rc.by_name,
          label: `${totalRej} pcs rejected — returned to pending for replacement`,
          lines: rejLines,
          reason: rc.rejection_reason,
        });
      }
    });
    if (p.status === "received" && p.received_at) {
      events.push({ type: "done", at: p.received_at, by: "", label: "Order fully received" });
    }
    return events.sort((a, b) => new Date(a.at) - new Date(b.at));
  };

  const iconFor = (t) => {
    if (t === "start") return <Flag size={16} weight="fill" className="text-zinc-800" />;
    if (t === "delivery") return <Truck size={16} weight="fill" className="text-[#16A34A]" />;
    if (t === "reject") return <XCircle size={16} weight="fill" className="text-[#DC2626]" />;
    return <CheckCircle size={16} weight="fill" className="text-[#16A34A]" />;
  };

  const ordered = po ? (po.items || []).reduce((s, it) => s + (it.quantity || 0), 0) : 0;
  const received = po ? (po.items || []).reduce((s, it) => s + (it.received_qty || 0), 0) : 0;
  const rejected = po ? (po.items || []).reduce((s, it) => s + (it.rejected_qty || 0), 0) : 0;

  return (
    <>
      <PageHeader
        title={po ? `${po.po_number || "Order"} Timeline` : "Order Timeline"}
        subtitle={po ? `${po.supplier_name} · ${received} / ${ordered} received · ${po.status}` : po === null ? "Not found" : "Loading…"}
      >
        {po && (
          <Button onClick={() => window.print()} data-testid="print-po-timeline" className="rounded-none bg-primary hover:bg-[#C2410C] text-white font-bold uppercase tracking-wider text-xs print:hidden">
            <Printer size={16} weight="bold" className="mr-1" /> Print
          </Button>
        )}
      </PageHeader>
      <div className="p-4 sm:p-8">
        {po === undefined ? (
          <p className="text-sm text-zinc-400">Loading…</p>
        ) : po === null ? (
          <p className="text-sm text-zinc-400" data-testid="po-timeline-not-found">This purchase order no longer exists.</p>
        ) : (
          <div className="bg-white border border-zinc-300 max-w-3xl">
            <div className="px-6 py-5 border-b border-zinc-300 bg-zinc-50">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="font-mono text-sm font-bold bg-zinc-900 text-white px-2 py-1">{po.po_number}</span>
                <span className="text-sm font-medium text-zinc-900">{po.supplier_name}</span>
                <span className={`px-2 py-0.5 text-[10px] font-bold uppercase text-white ${po.status === "received" ? "bg-[#16A34A]" : po.status === "partial" ? "bg-[#2563EB]" : "bg-[#EAB308]"}`}>{po.status}</span>
              </div>
              <div className="grid grid-cols-3 gap-4 mt-4 text-sm">
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Ordered</p><p className="font-mono font-bold text-lg">{ordered}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Received</p><p className="font-mono font-bold text-lg text-[#16A34A]">{received}</p></div>
                <div><p className="text-[11px] uppercase tracking-wider text-zinc-400">Rejected</p><p className="font-mono font-bold text-lg text-[#DC2626]">{rejected}</p></div>
              </div>
              {/* Fulfillment bar */}
              {ordered > 0 && (
                <div className="mt-4" data-testid="po-timeline-bar">
                  <div className="flex h-2.5 w-full overflow-hidden bg-zinc-200">
                    <div className="h-full bg-[#16A34A]" style={{ width: `${(received / ordered) * 100}%` }} title={`Received ${received}`} />
                    <div className="h-full bg-[#DC2626]" style={{ width: `${(rejected / ordered) * 100}%` }} title={`Rejected ${rejected}`} />
                  </div>
                  <div className="flex gap-4 mt-1.5 text-[11px] text-zinc-500">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#16A34A] inline-block" /> Received</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-[#DC2626] inline-block" /> Rejected</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 bg-zinc-200 inline-block" /> Awaiting</span>
                  </div>
                </div>
              )}
            </div>
            <div className="px-6 py-6" data-testid="po-timeline-events">
              <ol className="relative border-l border-zinc-200 ml-2">
                {buildEvents(po).map((e, i) => (
                  <li key={`${e.type}-${e.at}-${i}`} className="mb-6 ml-6 last:mb-0" data-testid={`po-timeline-event-${i}`}>
                    <span className="absolute -left-[9px] flex items-center justify-center w-[18px] h-[18px] bg-white">{iconFor(e.type)}</span>
                    <p className={`text-sm font-medium ${e.type === "reject" ? "text-[#DC2626]" : "text-zinc-900"}`}>{e.label}</p>
                    {e.sub && <p className="text-xs text-zinc-500">{e.sub}</p>}
                    {e.lines && e.lines.length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        {e.lines.map((l, li) => (
                          <span key={li} className={`font-mono text-[11px] px-2 py-0.5 border ${e.type === "reject" ? "border-[#FCA5A5] text-[#DC2626]" : "border-zinc-200 text-zinc-700"}`}>{l.name}: {l.q} {l.unit}</span>
                        ))}
                      </div>
                    )}
                    {e.reason && <p className="text-[11px] text-zinc-500 italic mt-0.5">Reason: {e.reason}</p>}
                    <p className="text-[11px] text-zinc-400 mt-0.5">{fmt(e.at)}{e.by ? ` · ${e.by}` : ""}</p>
                  </li>
                ))}
              </ol>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
